import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import BiscuitBrandLogo from '../common/BiscuitBrandLogo';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Footer link component with active state
const FooterLink = ({ to, children }) => {
    const location = useLocation();
    // Check if current path matches, or for shop categories check the full path
    const isActive = location.pathname === to || 
        (to.startsWith('/shop/') && location.pathname === to) ||
        (to === '/shop' && location.pathname === '/shop');
    
    return (
        <Link 
            to={to} 
            className={`transition-colors ${
                isActive 
                    ? 'text-primary font-semibold' 
                    : 'text-background/70 hover:text-primary'
            }`}
        >
            {children}
            {isActive && (
                <span className="block w-6 h-0.5 bg-primary mt-0.5 rounded-full" />
            )}
        </Link>
    );
};

const Footer = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        
        // Validate email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email.trim() || !emailRegex.test(email.trim())) {
            toast.error('Please enter a valid email address');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim() }),
            });

            if (response.ok) {
                const data = await response.json();
                // Show appropriate message based on status
                if (data.status === 'already_subscribed') {
                    toast.info(data.message);
                } else if (data.status === 'resubscribed') {
                    toast.success(data.message);
                } else {
                    toast.success(data.message);
                }
                setEmail('');
            } else {
                const data = await response.json();
                toast.error(data.detail || 'Subscription failed. Please try again.');
            }
        } catch (error) {
            toast.error('Subscription failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer className="bg-foreground text-background mt-auto">
            {/* Newsletter */}
            <div className="bg-primary py-12">
                <div className="container-custom">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div>
                            <h3 className="font-serif text-2xl text-primary-foreground mb-2">
                                Join Our Biscuit Club
                            </h3>
                            <p className="text-primary-foreground/80">
                                Get updates on new biscuits, exclusive finds, and global favourites.
                            </p>
                        </div>
                        <form className="flex w-full md:w-auto gap-2" onSubmit={handleSubscribe}>
                            <Input 
                                type="email" 
                                placeholder="Your email address" 
                                className="bg-white/10 border-white/20 text-white placeholder:text-white/60 w-full md:w-64"
                                data-testid="newsletter-email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                            />
                            <Button type="submit" variant="secondary" data-testid="newsletter-submit" disabled={loading}>
                                {loading ? 'Subscribing...' : 'Subscribe'}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Main Footer */}
            <div className="py-16">
                <div className="container-custom">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                        {/* Brand */}
                        <div>
                            <BiscuitBrandLogo 
                              variant="full" 
                              theme="light" 
                              size="lg" 
                              linkTo="/"
                              className="mb-6"
                            />
                            <p className="text-background/70">
                                The world's dedicated marketplace for biscuits. 
                                Connecting biscuit lovers with trusted sellers globally.
                            </p>
                        </div>

                        {/* Shop */}
                        <div>
                            <h4 className="font-semibold mb-4">Shop</h4>
                            <ul className="space-y-3">
                                <li><FooterLink to="/shop">All Biscuits</FooterLink></li>
                                <li><FooterLink to="/shop/chocolate-biscuits">Chocolate Biscuits</FooterLink></li>
                                <li><FooterLink to="/shop/shortbread">Shortbread</FooterLink></li>
                                <li><FooterLink to="/shop/cookies">Cookies</FooterLink></li>
                                <li><FooterLink to="/shop/gluten-free">Gluten-Free</FooterLink></li>
                            </ul>
                        </div>

                        {/* Company */}
                        <div>
                            <h4 className="font-semibold mb-4">Company</h4>
                            <ul className="space-y-3">
                                <li><FooterLink to="/about">About Us</FooterLink></li>
                                <li><FooterLink to="/contact">Contact</FooterLink></li>
                                <li><FooterLink to="/faq">FAQ</FooterLink></li>
                                <li><FooterLink to="/become-a-seller">Sell on Biscuit Brand</FooterLink></li>
                                <li><FooterLink to="/seller-policy">Seller Policy</FooterLink></li>
                            </ul>
                        </div>

                        {/* Contact */}
                        <div>
                            <h4 className="font-semibold mb-4">Contact Us</h4>
                            <ul className="space-y-3">
                                <li className="flex items-center gap-3 text-background/70">
                                    <Mail className="w-4 h-4" />
                                    hello@biscuitbrand.com
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-background/10 py-6">
                <div className="container-custom">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-background/60 text-sm">
                            © {new Date().getFullYear()} Biscuit Brand — Crafted for biscuit lovers worldwide.
                        </p>
                        <div className="flex gap-6 text-sm">
                            <FooterLink to="/terms">Terms & Conditions</FooterLink>
                            <FooterLink to="/privacy">Privacy Policy</FooterLink>
                            <FooterLink to="/returns">Returns Policy</FooterLink>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
